/**
 * Company Pinia Store
 */
import { defineStore } from 'pinia'
import { ref, computed, readonly, watch } from 'vue'
import { useUserStore } from '~/stores/user'

export const useCompanyStore = defineStore('company', () => {
  const currentCompany = ref<any>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref(0)
  const CACHE_TTL = 5 * 60 * 1000

  const isDataStale = computed(() => Date.now() - lastFetchTime.value > CACHE_TTL)
  const hasCurrentCompany = computed(() => !!currentCompany.value?.id)

  const fetchCompanyData = async (force = false) => {
    if (!force && !isDataStale.value && currentCompany.value) return currentCompany.value
    if (isLoading.value) {
      while (isLoading.value) await new Promise(resolve => setTimeout(resolve, 100))
      return currentCompany.value
    }

    try {
      isLoading.value = true
      error.value = null
      const response = await $fetch<{company: any | null}>('/api/companies/current')
      if (response?.company) {
        currentCompany.value = response.company
        lastFetchTime.value = Date.now()
      } else {
        currentCompany.value = null
      }
      return currentCompany.value
    } catch (err: any) {
      console.error('Failed to fetch company data:', err)
      error.value = err.message || 'Failed to fetch company data'
      if (!currentCompany.value) currentCompany.value = null
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearCompanyData = () => {
    currentCompany.value = null; lastFetchTime.value = 0; error.value = null
  }

  const updateCompanyData = (companyData: any) => {
    currentCompany.value = { ...currentCompany.value, ...companyData }
    lastFetchTime.value = Date.now()
  }

  return {
    currentCompany: readonly(currentCompany), isLoading: readonly(isLoading), error: readonly(error),
    isDataStale: readonly(isDataStale), hasCurrentCompany: readonly(hasCurrentCompany),
    fetchCompanyData, clearCompanyData, updateCompanyData
  }
})

export const useCompanyState = () => {
  const companyStore = useCompanyStore()
  const userStore = useUserStore()

  const initializeCompany = async () => {
    if (userStore.isAuthenticated && companyStore.isDataStale) {
      await companyStore.fetchCompanyData()
    }
  }

  if (process.client) {
    watch(() => userStore.isAuthenticated, (isAuthenticated) => {
      if (isAuthenticated) initializeCompany()
      else companyStore.clearCompanyData()
    }, { immediate: true })
  }

  return {
    currentCompany: companyStore.currentCompany, isLoading: companyStore.isLoading,
    error: companyStore.error, hasCurrentCompany: companyStore.hasCurrentCompany,
    fetchCompanyData: companyStore.fetchCompanyData, clearCompanyData: companyStore.clearCompanyData,
    updateCompanyData: companyStore.updateCompanyData, initializeCompany
  }
}
